import { parseJsonLenient } from '@/shared/utils/helpers';

/**
 * "When evaluating" output-cleanup flags (Model Configuration). Mirrors the
 * backend semantics in optuneutils.metrics_calculation.evaluation_normalization
 * for the client-side "Evaluated as" preview chip.
 */

export const DEFAULT_EVALUATION_NORMALIZATION = {
    normalizeCasing: false,
    stripWhitespace: false,
    fixJsonFormat: false,
};

/** Shared labels for the Model Configuration checkboxes and the version Summary. */
export const EVALUATION_NORMALIZATION_OPTIONS = [
    {
        key: 'normalizeCasing',
        field: 'evaluation_normalize_casing',
        label: 'Normalize casing',
        help: 'Compare text without case differences (e.g., "Yes" = "yes").',
    },
    {
        key: 'stripWhitespace',
        field: 'evaluation_strip_whitespace',
        label: 'Strip whitespace',
        help: 'Remove extra spaces at the start and end of responses.',
    },
    {
        key: 'fixJsonFormat',
        field: 'evaluation_fix_json_format',
        label: 'Fix JSON format',
        help: 'Repair common JSON formatting issues before evaluation.',
    },
];

export const evaluationNormalizationFromUsecase = (usecase) => ({
    normalizeCasing: Boolean(usecase?.evaluation_normalize_casing),
    stripWhitespace: Boolean(usecase?.evaluation_strip_whitespace),
    fixJsonFormat: Boolean(usecase?.evaluation_fix_json_format),
});

export const evaluationNormalizationEqual = (a, b) =>
    Boolean(a?.normalizeCasing) === Boolean(b?.normalizeCasing) &&
    Boolean(a?.stripWhitespace) === Boolean(b?.stripWhitespace) &&
    Boolean(a?.fixJsonFormat) === Boolean(b?.fixJsonFormat);

export const evaluationNormalizationToPayload = (normalization) => ({
    evaluation_normalize_casing: Boolean(normalization?.normalizeCasing),
    evaluation_strip_whitespace: Boolean(normalization?.stripWhitespace),
    evaluation_fix_json_format: Boolean(normalization?.fixJsonFormat),
});

/**
 * Classification label catalog the "Evaluated as" chip matches against.
 * Same preference as ``training_setup.get_label_names``: dataset count-map keys
 * when any exist, otherwise the labels typed on the task.
 */
export const evaluationLabelNamesFromUsecase = (usecase) => {
    const counts = usecase?.label_names_count;
    if (counts && typeof counts === 'object' && Object.keys(counts).length > 0) {
        return Object.keys(counts).filter((name) => name);
    }
    if (!Array.isArray(usecase?.manually_added_labels)) return [];
    return usecase.manually_added_labels.filter((name) => typeof name === 'string' && name);
};

const repairJsonText = (value) => {
    try {
        const parsed = parseJsonLenient(value);
        if (parsed !== null && typeof parsed === 'object') {
            return JSON.stringify(parsed);
        }
    } catch {
        // Not repairable — keep the raw value.
    }
    return value;
};

/**
 * Comparison key for one string. JSON repair runs only for the model output;
 * task labels are matched on their own spelling after strip / casing.
 */
const evaluationComparisonKey = (value, normalization, { repairJson = false } = {}) => {
    let result = value;
    if (repairJson && normalization?.fixJsonFormat) {
        result = repairJsonText(result);
    }
    if (normalization?.stripWhitespace) {
        result = result.trim();
    }
    if (normalization?.normalizeCasing) {
        result = result.toLowerCase();
    }
    return result;
};

const normalizationEnabled = (normalization) =>
    Boolean(normalization?.normalizeCasing) ||
    Boolean(normalization?.stripWhitespace) ||
    Boolean(normalization?.fixJsonFormat);

/**
 * Apply the cleanup flags to one output value for the "Evaluated as" preview.
 * Fix JSON first (canonical form), then strip, then casing — same order as the
 * backend applies them at evaluation time.
 *
 * When ``labelNames`` is a non-empty task catalog, a cleaned value that matches
 * a label snaps to that label's original spelling (first label wins). An
 * unmatched value stays unchanged so the chip does not invent a string that is
 * not one of the task labels. With no catalog (summarization / content
 * generation) the cleaned string itself is the preview.
 */
export const applyEvaluationNormalizationPreview = (value, normalization, labelNames) => {
    if (typeof value !== 'string') return value;
    const names = Array.isArray(labelNames)
        ? labelNames.filter((name) => typeof name === 'string' && name)
        : [];
    if (names.length === 0) {
        return evaluationComparisonKey(value, normalization, { repairJson: true });
    }
    if (!normalizationEnabled(normalization)) return value;
    const key = evaluationComparisonKey(value, normalization, { repairJson: true });
    for (const name of names) {
        if (evaluationComparisonKey(name, normalization) === key) return name;
    }
    return value;
};
